/**
 * ClientRecordsDatabase.tsx — Every raw client record with match status.
 * Rebuilt in Pass 3.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import {
  PageHeader,
  Button, Icon,
  SearchInput, Select,
  Table, TableToolbar, Pagination,
  MethodTag, ConfidenceTag,
  EmptyState,
  toast,
} from '../../components/ui';

interface ClientRecord {
  id: string;
  client_name: string;
  raw_name: string;
  raw_address: string;
  raw_city: string;
  raw_vat: string;
  census_id: string;
  match_method: string;
  match_confidence: number | null;
  match_status: string;
  canonical_name: string;
  canonical_city: string;
  created_at: string | null;
}

interface RecordsResponse {
  items: ClientRecord[];
  total: number;
  page: number;
  pages: number;
}

const LIMIT = 25;

export default function ClientRecordsDatabase() {
  const navigate = useNavigate();

  const [data, setData] = useState<RecordsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [client, setClient] = useState('');
  const [matchMethod, setMatchMethod] = useState('');
  const [clients, setClients] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    document.title = 'Client Records | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  useEffect(() => {
    api.get<string[]>('/api/clients').then(r => setClients(r.data || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (search) params.set('search', search);
      if (client) params.set('client', client);
      if (matchMethod) params.set('match_method', matchMethod);
      const res = await api.get<RecordsResponse>(`/api/client-records?${params}`, { signal });
      setData(res.data);
    } catch (err: unknown) {
      if (err instanceof Error && (err.name === 'CanceledError' || ('code' in err && (err as { code?: string }).code === 'ERR_CANCELED'))) return;
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [page, search, client, matchMethod]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const handleSearchInput = (val: string) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val.trim()); setPage(1); }, 300);
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;

  const columns = [
    { key: 'client_name', header: 'Client', render: (r: ClientRecord) => <span style={{ color: theme.text.secondary, fontSize: theme.fontSize.sm }}>{r.client_name}</span>, nowrap: true },
    { key: 'raw_name', header: 'Submitted Name', render: (r: ClientRecord) => r.raw_name || null },
    { key: 'raw_address', header: 'Address', render: (r: ClientRecord) => <span style={{ color: theme.text.secondary, fontSize: theme.fontSize.sm }}>{r.raw_address || '—'}</span> },
    { key: 'raw_city', header: 'City', render: (r: ClientRecord) => <span style={{ color: theme.text.secondary }}>{r.raw_city || '—'}</span> },
    { key: 'raw_vat', header: 'VAT', mono: true, render: (r: ClientRecord) => <span style={{ color: theme.text.tertiary, fontSize: theme.fontSize.sm }}>{r.raw_vat || '—'}</span> },
    {
      key: 'census_id',
      header: 'Census ID',
      mono: true,
      nowrap: true,
      render: (r: ClientRecord) => r.census_id ? (
        <span
          onClick={(e) => { e.stopPropagation(); navigate(`/outlet/${r.census_id}`); }}
          style={{ color: theme.brand.cyanLight, cursor: 'pointer', fontFamily: theme.font.data, fontSize: theme.fontSize.sm }}
          onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
          onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
        >
          {r.census_id}
        </span>
      ) : <span style={{ color: theme.text.quaternary }}>—</span>,
    },
    { key: 'method', header: 'Method', render: (r: ClientRecord) => <MethodTag method={r.match_method} />, nowrap: true },
    { key: 'confidence', header: 'Confidence', align: 'right' as const, render: (r: ClientRecord) => <ConfidenceTag score={r.match_confidence} />, nowrap: true },
    { key: 'outlet_name', header: 'Outlet Name', render: (r: ClientRecord) => <span style={{ color: theme.text.secondary, fontSize: theme.fontSize.sm }}>{r.canonical_name || '—'}</span> },
  ];

  return (
    <div style={{ padding: `${theme.space[8]} ${theme.space[10]}`, maxWidth: 1600, margin: '0 auto' }}>
      <PageHeader
        title="Client Records"
        description="Every raw client record with its match status."
        meta={
          <>
            <span style={{ color: theme.text.primary, fontWeight: theme.fontWeight.medium }}>
              {total.toLocaleString()}
            </span>
            <span>records</span>
          </>
        }
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: theme.space[3],
        marginBottom: theme.space[5],
      }}>
        <SearchInput
          placeholder="Search name, address, city…"
          value={searchInput}
          onChange={e => handleSearchInput(e.target.value)}
          kbd={undefined}
        />
        <Select value={client} onChange={e => { setClient(e.target.value); setPage(1); }}>
          <option value="">All clients</option>
          {clients.map(c => <option key={c} value={c}>{c}</option>)}
        </Select>
        <Select value={matchMethod} onChange={e => { setMatchMethod(e.target.value); setPage(1); }}>
          <option value="">All match types</option>
          <option value="deterministic">Exact match</option>
          <option value="fuzzy">Smart match</option>
          <option value="geo">GPS match</option>
          <option value="manual_approved">Manual approved</option>
          <option value="llm_approved">AI approved</option>
          <option value="unmatched">Unmatched</option>
        </Select>
      </div>

      <TableToolbar>
        <div style={{ color: theme.text.tertiary, fontSize: theme.fontSize.sm }}>
          Showing{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {items.length.toLocaleString()}
          </span>
          {' '}of{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {total.toLocaleString()}
          </span>
        </div>
        <Pagination page={page} totalPages={pages} onChange={setPage} />
      </TableToolbar>

      <Table
        data={items}
        columns={columns}
        loading={loading}
        minWidth={1200}
        rowKey={r => r.id}
        emptyState={
          <EmptyState
            icon={<Icon.FileText size={18} />}
            title="No records found"
            description="Try clearing filters or adjusting your search."
            action={(search || client || matchMethod) ? (
              <Button variant="secondary" size="sm"
                onClick={() => { setSearchInput(''); setSearch(''); setClient(''); setMatchMethod(''); setPage(1); }}>
                Clear filters
              </Button>
            ) : undefined}
          />
        }
      />
    </div>
  );
}
